/**
 * StormIntelCard — drop-in card showing recent severe-weather activity near
 * a property. Used on LeadDetail (per-house view) and inside the EstimateBuilder
 * sidebar so contractors instantly see if the prospect just got hit.
 *
 * Data source is /api/storms/history (which proxies NOAA LSR via IEM). The
 * component handles loading/empty/error states inline so consumers can drop
 * it in without managing any state.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Cloud, CloudHail, Wind, Tornado, Zap, Snowflake, Waves, Loader2, AlertCircle, ExternalLink, MapPin, CalendarClock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

const CATEGORY_ICONS = {
    hail: CloudHail,
    wind: Wind,
    tornado: Tornado,
    hurricane: Cloud,
    lightning: Zap,
    ice: Snowflake,
    flood: Waves,
};
const CATEGORY_COLORS = {
    hail: { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', icon: 'text-sky-600' },
    wind: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'text-amber-600' },
    tornado: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', icon: 'text-rose-600' },
    hurricane: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', icon: 'text-purple-600' },
    lightning: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', icon: 'text-yellow-600' },
    ice: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', icon: 'text-cyan-600' },
    flood: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'text-blue-600' },
};

export function StormIntelCard({
    lat,
    lng,
    state,
    radiusMiles = 10,
    daysBack = 730,
    title = 'Storm activity near this property',
    compact = false,
    className = '',
}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandEvents, setExpandEvents] = useState(false);

    useEffect(() => {
        if (typeof lat !== 'number' || typeof lng !== 'number') {
            setData(null);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({
            lat: String(lat),
            lng: String(lng),
            radius_miles: String(radiusMiles),
            days_back: String(daysBack),
        });
        if (state) params.set('state', state);
        api.get(`/storms/history?${params.toString()}`)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch((e) => { if (!cancelled) setError(e?.message || 'Could not load storm data.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [lat, lng, state, radiusMiles, daysBack]);

    const monthsBack = useMemo(() => Math.round(daysBack / 30), [daysBack]);
    const radiusLabel = `${radiusMiles}-mile`;

    // Skeleton loading state
    if (loading || (!data && !error)) {
        return (
            <Card className={`border-slate-200 ${className}`}>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-display flex items-center gap-2">
                        <Cloud className="h-4 w-4 text-slate-400" /> {title}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Loading {monthsBack}-month storm history…
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (error || data?.ok === false) {
        const note = data?.note || error;
        return (
            <Card className={`border-slate-200 ${className}`}>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-display flex items-center gap-2">
                        <Cloud className="h-4 w-4 text-slate-400" /> {title}
                    </CardTitle>
                </CardHeader>
                <CardContent className="text-xs text-slate-500 flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
                    <span>Could not load storm data{note ? `: ${note}` : '.'} Try again in a few minutes — NOAA LSR can be slow during heavy weather.</span>
                </CardContent>
            </Card>
        );
    }

    const s = data?.summary || {};
    const events = data?.events || [];
    const total = s.total_events || 0;

    // Quiet area — clean empty state
    if (total === 0) {
        return (
            <Card className={`border-emerald-100 bg-emerald-50/40 ${className}`} data-testid="storm-intel-empty">
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-display flex items-center gap-2">
                        <Cloud className="h-4 w-4 text-emerald-600" /> {title}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-xs text-slate-600 leading-relaxed">
                        No significant severe weather reported within {radiusLabel} of this property in the last {monthsBack} months.
                        That&apos;s a quiet record for the area — the homeowner may not be storm-claim shopping right now.
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={`border-slate-200 ${className}`} data-testid="storm-intel-card">
            <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                    <CardTitle className="text-sm font-display flex items-center gap-2">
                        <CloudHail className="h-4 w-4 text-sky-600" /> {title}
                    </CardTitle>
                    <Badge variant="outline" className="text-[10px] font-normal bg-slate-50 border-slate-200">
                        Last {monthsBack} mo · {radiusLabel}
                    </Badge>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* Headline stat */}
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Severe weather events</div>
                    <div className="flex items-end gap-2 mt-0.5">
                        <span className="font-display text-2xl text-slate-900 tabular-nums" data-testid="storm-intel-total">{total}</span>
                        <span className="text-xs text-slate-600 pb-1">
                            within {radiusLabel}
                            {s.most_recent && (
                                <> · most recent <b className="text-slate-900">{formatWhen(s.most_recent)}</b></>
                            )}
                        </span>
                    </div>
                </div>

                {/* Per-category breakdown */}
                {!compact && (s.by_category?.length || 0) > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                        {s.by_category.map((c) => {
                            const Icon = CATEGORY_ICONS[c.category] || Cloud;
                            const col = CATEGORY_COLORS[c.category] || CATEGORY_COLORS.wind;
                            const unit = c.category === 'hail' ? '"' : (c.category === 'wind' ? ' mph' : '');
                            return (
                                <div
                                    key={c.category}
                                    className={`rounded-md border ${col.border} ${col.bg} px-2 py-1.5 flex items-center gap-2`}
                                    data-testid={`storm-intel-cat-${c.category}`}
                                >
                                    <Icon className={`h-3.5 w-3.5 ${col.icon} shrink-0`} />
                                    <div className="min-w-0">
                                        <div className={`text-[10px] uppercase tracking-wider ${col.text} font-semibold leading-tight`}>{c.label}</div>
                                        <div className="text-xs text-slate-900 tabular-nums">
                                            <b>{c.count}</b>
                                            {c.max_magnitude > 0 && (
                                                <span className="text-slate-500 text-[10px] ml-1">max {c.max_magnitude}{unit}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Top hail + wind callouts when present */}
                {(s.max_hail_in > 0 || s.max_wind_mph > 0) && !compact && (
                    <div className="text-[11px] text-slate-600 leading-relaxed flex items-start gap-1.5">
                        <AlertCircle className="h-3 w-3 text-amber-500 shrink-0 mt-0.5" />
                        <span>
                            Worst case in this area:&nbsp;
                            {s.max_hail_in > 0 && <b className="text-slate-900">{s.max_hail_in}&quot; hail</b>}
                            {s.max_hail_in > 0 && s.max_wind_mph > 0 && <>, </>}
                            {s.max_wind_mph > 0 && <b className="text-slate-900">{s.max_wind_mph} mph winds</b>}
                            . Strong storm-claim talking point for the homeowner.
                        </span>
                    </div>
                )}

                {/* Recent event list (expandable) */}
                {!compact && events.length > 0 && (
                    <>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandEvents((v) => !v)}
                            className="h-7 text-xs text-slate-700 hover:bg-slate-100 -ml-2"
                            data-testid="storm-intel-toggle-events"
                        >
                            {expandEvents ? 'Hide' : 'Show'} {events.length} event{events.length === 1 ? '' : 's'}
                        </Button>
                        {expandEvents && (
                            <div className="rounded-md border border-slate-200 max-h-72 overflow-y-auto divide-y divide-slate-100" data-testid="storm-intel-events">
                                {events.map((e, i) => {
                                    const Icon = CATEGORY_ICONS[e.category] || Cloud;
                                    const col = CATEGORY_COLORS[e.category] || CATEGORY_COLORS.wind;
                                    return (
                                        <div key={i} className="px-3 py-2 text-xs flex items-start gap-2.5">
                                            <Icon className={`h-3.5 w-3.5 mt-0.5 ${col.icon} shrink-0`} />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                                    <span className="font-medium text-slate-900">{e.event}</span>
                                                    {e.magnitude != null && e.magnitude > 0 && (
                                                        <span className="text-[10px] text-slate-600 tabular-nums">
                                                            {e.magnitude}{e.magnitude_unit ? ` ${e.magnitude_unit}` : ''}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="mt-0.5 text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
                                                    <span className="inline-flex items-center gap-0.5"><CalendarClock className="h-2.5 w-2.5" /> {formatWhen(e.when)}</span>
                                                    {e.city && <span className="inline-flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" /> {e.city}{e.county ? `, ${e.county} co` : ''}</span>}
                                                </div>
                                                {e.remark && (
                                                    <div className="mt-1 text-[11px] text-slate-600 leading-relaxed italic">&ldquo;{e.remark}&rdquo;</div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                <div className="text-[10px] text-slate-400 inline-flex items-center gap-1">
                    Source: NOAA Local Storm Reports via Iowa State Mesonet
                    <ExternalLink className="h-2.5 w-2.5" />
                </div>
            </CardContent>
        </Card>
    );
}

function formatWhen(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const now = new Date();
        const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
        if (diffDays < 1) return 'today';
        if (diffDays < 2) return 'yesterday';
        if (diffDays < 14) return `${diffDays} days ago`;
        if (diffDays < 60) return `${Math.floor(diffDays / 7)} weeks ago`;
        if (diffDays < 730) return `${Math.floor(diffDays / 30)} months ago`;
        return `${Math.floor(diffDays / 365)} years ago`;
    } catch {
        return iso;
    }
}
